import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, Edit, Trash2, Phone, MapPin, Calendar, Heart, Users, Save, Plus, MessageSquare, PhoneCall, Mail, Eye, Send, Camera } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const followUpIcons = { text: MessageSquare, call: PhoneCall, card: Mail, visit: Eye, social_media: Send };
const followUpTypes = ['text', 'call', 'card', 'visit', 'social_media'];

export default function PersonProfile() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [person, setPerson] = useState(null);
  const [families, setFamilies] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [notes, setNotes] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [fuOpen, setFuOpen] = useState(false);
  const [fuForm, setFuForm] = useState({ action_type: 'call', date: new Date().toISOString().slice(0, 10), note: '' });
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const canSeePastoral = user?.role === 'admin' || user?.role === 'pastor';

  const generalNotes = useMemo(() => notes.filter(n => n.note_type === 'general'), [notes]);
  const pastoralNotes = useMemo(() => notes.filter(n => n.note_type === 'pastoral'), [notes]);
  const addressLine = useMemo(() => [person?.city, person?.state, person?.zip_code].filter(Boolean).join(', '), [person]);

  useEffect(() => {
    Promise.all([
      api.get(`/people/${personId}`),
      api.get('/families'),
      api.get('/ministries'),
      api.get(`/followups/${personId}`),
      api.get(`/notes/${personId}`),
      api.get(`/attendance/person/${personId}`),
    ]).then(([p, f, m, fu, n, a]) => {
      setPerson(p.data);
      setEditForm(p.data);
      setFamilies(f.data);
      setMinistries(m.data);
      setFollowUps(fu.data);
      setNotes(n.data);
      setAttendance(a.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [personId]);

  const handleSave = async () => {
    try {
      const res = await api.put(`/people/${personId}`, editForm);
      setPerson(res.data);
      setEditing(false);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== 'CONFIRM DELETE') { toast.error("Type 'CONFIRM DELETE' to proceed"); return; }
    try {
      await api.delete(`/people/${personId}`);
      toast.success('Person archived');
      navigate('/people');
    } catch { toast.error('Failed to delete'); }
  };

  const toggleEditMinistry = (name) => {
    setEditForm(f => ({
      ...f,
      ministries: (f.ministries || []).includes(name)
        ? f.ministries.filter(m => m !== name)
        : [...(f.ministries || []), name]
    }));
  };

  const addFollowUp = async () => {
    try {
      const res = await api.post(`/followups/${personId}`, fuForm);
      setFollowUps(prev => [res.data, ...prev]);
      setFuOpen(false);
      setFuForm({ action_type: 'call', date: new Date().toISOString().slice(0, 10), note: '' });
      toast.success('Follow-up logged');
    } catch { toast.error('Failed to add follow-up'); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await api.post(`/notes/${personId}`, { content: noteText, note_type: noteType });
      setNotes(prev => [res.data, ...prev]);
      setNoteText('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const familyName = families.find(f => f.family_id === person?.family_id)?.family_name;

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;
  if (!person) return <div className="text-center py-20 text-slate-500">Person not found</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" onClick={() => navigate('/people')} className="text-slate-600 -ml-2" data-testid="back-to-people">
        <ArrowLeft className="h-4 w-4 mr-1" />Back to People
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="text-center mb-4">
              <div className="relative inline-block">
                {person.photo_path ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${person.photo_path}`} alt="" className="h-20 w-20 rounded-full object-cover mx-auto" />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-800 mx-auto">{person.first_name[0]}{(person.last_name || '')[0] || ''}</div>
                )}
                <label className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-blue-700 flex items-center justify-center cursor-pointer hover:bg-blue-800 transition-colors shadow-md">
                  <Camera className="h-3.5 w-3.5 text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      await api.post(`/people/${personId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const res = await api.get(`/people/${personId}`);
                      setPerson(res.data);
                      toast.success('Photo updated');
                    } catch { toast.error('Failed to upload photo'); }
                  }} />
                </label>
              </div>
              <h2 data-testid="person-name" className="text-xl font-semibold text-slate-900 mt-3 font-['Outfit']">{person.first_name} {person.last_name}</h2>
              <Badge variant="outline" className={`mt-1 ${person.person_type === 'regular' ? 'border-blue-200 text-blue-700 bg-blue-50' : 'border-amber-200 text-amber-700 bg-amber-50'}`}>
                {person.person_type}
              </Badge>
            </div>
            <div className="space-y-3 text-sm">
              {person.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" />{person.phone}</div>}
              {person.secondary_phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /><span className="text-xs text-slate-400 mr-1">2nd</span>{person.secondary_phone}</div>}
              {person.email_address && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" />{person.email_address}</div>}
              {(person.address || person.city) && <div className="flex items-start gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /><div>{person.address}{person.address && (person.city || person.state) ? <br/> : ''}{[person.city, person.state, person.zip_code].filter(Boolean).join(', ')}</div></div>}
              {person.birthday && <div className="flex items-center gap-2 text-slate-600"><Calendar className="h-4 w-4 text-slate-400" />Birthday: {person.birthday}</div>}
              {person.anniversary && <div className="flex items-center gap-2 text-slate-600"><Heart className="h-4 w-4 text-slate-400" />Anniversary: {person.anniversary}</div>}
              {familyName && <div className="flex items-center gap-2 text-slate-600"><Users className="h-4 w-4 text-slate-400" />{familyName} Family</div>}
            </div>
            {person.ministries?.length > 0 && (
              <div className="mt-4"><p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Ministries</p>
                <div className="flex flex-wrap gap-1">{person.ministries.map(m => <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>)}</div>
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <Button data-testid="edit-person" size="sm" variant="outline" onClick={() => setEditing(true)} className="flex-1 border-slate-300"><Edit className="h-3 w-3 mr-1" />Edit</Button>
              {(user?.role === 'admin' || user?.role === 'pastor') && (
                <AlertDialog open={deleteStep > 0} onOpenChange={(open) => { if (!open) { setDeleteStep(0); setDeleteConfirmText(''); } }}>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="delete-person" size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteStep(1)}><Trash2 className="h-3 w-3" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{deleteStep === 1 ? 'Archive this person?' : 'Final Confirmation'}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {deleteStep === 1 ? (
                          <span>This will archive {person.first_name} {person.last_name}. Their records will be preserved but they won't appear in active lists.</span>
                        ) : (
                          <div className="space-y-3">
                            <p>Type <strong>CONFIRM DELETE</strong> to proceed:</p>
                            <Input data-testid="delete-confirm-input" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="CONFIRM DELETE" />
                          </div>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => { setDeleteStep(0); setDeleteConfirmText(''); }}>Cancel</AlertDialogCancel>
                      {deleteStep === 1 ? (
                        <AlertDialogAction data-testid="confirm-delete-step1" onClick={(e) => { e.preventDefault(); setDeleteStep(2); }} className="bg-red-600 hover:bg-red-700">Continue</AlertDialogAction>
                      ) : (
                        <Button data-testid="confirm-delete-step2" onClick={handleDelete} disabled={deleteConfirmText !== 'CONFIRM DELETE'} className="bg-red-600 hover:bg-red-700 disabled:opacity-50">Archive</Button>
                      )}
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Tabs defaultValue="followups">
            <TabsList className="bg-slate-100 mb-4">
              <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
              <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
              {canSeePastoral && <TabsTrigger value="pastoral" data-testid="tab-pastoral">Pastoral Care</TabsTrigger>}
            </TabsList>

            <TabsContent value="followups">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Outfit']">Follow-Up History</CardTitle>
                    <Button data-testid="add-followup" size="sm" onClick={() => setFuOpen(true)} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-3 w-3 mr-1" />Log Action</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {followUps.length === 0 && <p className="text-sm text-slate-500 py-4">No follow-up actions logged yet</p>}
                  <div className="space-y-3">
                    {followUps.map(fu => {
                      const Icon = followUpIcons[fu.action_type] || MessageSquare;
                      return (
                        <div key={fu.followup_id} className="flex gap-3 p-3 rounded-lg bg-slate-50">
                          <div className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-blue-700" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs capitalize">{fu.action_type.replace('_', ' ')}</Badge>
                              <span className="text-xs text-slate-500">{new Date(fu.date + 'T00:00:00').toLocaleDateString()}</span>
                            </div>
                            {fu.note && <p className="text-sm text-slate-700 mt-1">{fu.note}</p>}
                            <p className="text-xs text-slate-400 mt-1">by {fu.performed_by_name}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-lg font-['Outfit']">General Notes</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Textarea data-testid="note-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className="border-slate-300 min-h-[60px]" />
                    <Button data-testid="add-note" onClick={() => { setNoteType('general'); addNote(); }} className="bg-blue-700 hover:bg-blue-800 shrink-0">Add</Button>
                  </div>
                  {generalNotes.map(n => (
                    <div key={n.note_id} className="p-3 rounded-lg bg-slate-50 mb-2">
                      <p className="text-sm text-slate-700">{n.content}</p>
                      <p className="text-xs text-slate-400 mt-1">{n.created_by_name} - {new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                  {person.general_notes && (
                    <div className="p-3 rounded-lg bg-slate-50 mb-2">
                      <p className="text-sm text-slate-700">{person.general_notes}</p>
                      <p className="text-xs text-slate-400 mt-1">Initial notes</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="attendance">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-lg font-['Outfit']">Attendance Record</CardTitle></CardHeader>
                <CardContent>
                  {attendance.length === 0 && <p className="text-sm text-slate-500 py-4">No attendance records</p>}
                  <div className="space-y-2">
                    {attendance.map((a) => (
                      <div key={`${a.date}-${a.event_id || a.record_id || a.date}`} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700">{new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <Badge className={`text-xs ${a.present ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}`}>
                          {a.present ? 'Present' : 'Absent'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {canSeePastoral && (
              <TabsContent value="pastoral">
                <Card className="border-slate-200 shadow-sm border-l-4 border-l-violet-400">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Heart className="h-4 w-4 text-violet-500" />Pastoral Care Notes</CardTitle>
                    <p className="text-xs text-slate-500">Visible only to Admin and Pastor roles</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 mb-4">
                      <Textarea data-testid="pastoral-note-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add pastoral care note..." className="border-slate-300 min-h-[60px]" />
                      <Button data-testid="add-pastoral-note" onClick={() => { setNoteType('pastoral'); addNote(); }} className="bg-violet-600 hover:bg-violet-700 shrink-0">Add</Button>
                    </div>
                    {pastoralNotes.map(n => (
                      <div key={n.note_id} className="p-3 rounded-lg bg-violet-50 mb-2 border border-violet-200">
                        <p className="text-sm text-slate-700">{n.content}</p>
                        <p className="text-xs text-slate-400 mt-1">{n.created_by_name} - {new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                    {notes.filter(n => n.note_type === 'pastoral').length === 0 && <p className="text-sm text-slate-500 py-2">No pastoral care notes</p>}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">Log Follow-Up Action</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Action Type</Label>
              <Select value={fuForm.action_type} onValueChange={v => setFuForm(f => ({ ...f, action_type: v }))}>
                <SelectTrigger data-testid="followup-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{followUpTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input data-testid="followup-date" type="date" value={fuForm.date} onChange={e => setFuForm(f => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
            <div><Label>Notes</Label><Textarea data-testid="followup-note" value={fuForm.note} onChange={e => setFuForm(f => ({ ...f, note: e.target.value }))} className="mt-1" placeholder="Optional details..." /></div>
            <Button data-testid="followup-submit" onClick={addFollowUp} className="w-full bg-blue-700 hover:bg-blue-800">Log Follow-Up</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit Profile</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>First Name</Label><Input data-testid="edit-first-name" value={editForm.first_name || ''} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} className="mt-1" /></div>
            <div><Label>Last Name</Label><Input data-testid="edit-last-name" value={editForm.last_name || ''} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} className="mt-1" /></div>
            <div><Label>Phone</Label><Input data-testid="edit-phone" value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
            <div><Label>Secondary Phone</Label><Input data-testid="edit-secondary-phone" value={editForm.secondary_phone || ''} onChange={e => setEditForm(f => ({ ...f, secondary_phone: e.target.value }))} className="mt-1" /></div>
            <div className="col-span-2"><Label>Email Address</Label><Input data-testid="edit-email" type="email" value={editForm.email_address || ''} onChange={e => setEditForm(f => ({ ...f, email_address: e.target.value }))} className="mt-1" /></div>
            <div className="col-span-2"><Label>Address</Label><Input data-testid="edit-address" value={editForm.address || ''} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} className="mt-1" /></div>
            <div><Label>City</Label><Input data-testid="edit-city" value={editForm.city || ''} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State</Label><Input data-testid="edit-state" value={editForm.state || ''} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} className="mt-1" /></div>
              <div><Label>Zip</Label><Input data-testid="edit-zip" value={editForm.zip_code || ''} onChange={e => setEditForm(f => ({ ...f, zip_code: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Birthday</Label><Input data-testid="edit-birthday" type="date" value={editForm.birthday || ''} onChange={e => setEditForm(f => ({ ...f, birthday: e.target.value }))} className="mt-1" /></div>
            <div><Label>Anniversary</Label><Input data-testid="edit-anniversary" type="date" value={editForm.anniversary || ''} onChange={e => setEditForm(f => ({ ...f, anniversary: e.target.value }))} className="mt-1" /></div>
            <div>
              <Label>Type</Label>
              <Select value={editForm.person_type} onValueChange={v => setEditForm(f => ({ ...f, person_type: v }))}>
                <SelectTrigger data-testid="edit-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visitor">Visitor</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="occasional">Occasional</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Family</Label>
              <Select value={editForm.family_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, family_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="edit-family" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Family</SelectItem>
                  {families.map(f => <SelectItem key={f.family_id} value={f.family_id}>{f.family_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>General Notes</Label><Textarea data-testid="edit-notes" value={editForm.general_notes || ''} onChange={e => setEditForm(f => ({ ...f, general_notes: e.target.value }))} className="mt-1" /></div>
            <div className="col-span-2">
              <Label>Ministries</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ministries.map(m => (
                  <label key={m.ministry_id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${(editForm.ministries || []).includes(m.name) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    <Checkbox checked={(editForm.ministries || []).includes(m.name)} onCheckedChange={() => toggleEditMinistry(m.name)} className="h-3.5 w-3.5" />
                    {m.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <Button data-testid="save-profile" onClick={handleSave} className="w-full mt-2 bg-blue-700 hover:bg-blue-800"><Save className="h-4 w-4 mr-1" />Save Changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
